import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, CheckCircle2, AlertCircle, Search, X, Camera, Loader2, CloudUpload } from "lucide-react";
import { toast } from "sonner";

/** Public Billing Form — token via `/billing/{google_event_id}` (no login).
 *  Sections: Header · Rincian (search+dropdown) · Payment · Catatan · Foto · Submit.
 */
const BASE = process.env.REACT_APP_BACKEND_URL;
const pub = axios.create({ baseURL: `${BASE}/api/public`, timeout: 30000 });
const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

/** Compress image via <canvas> to keep mobile uploads small. */
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        if (width >= height) { height = Math.round(height * maxDim / width); width = maxDim; }
        else { width = Math.round(width * maxDim / height); height = maxDim; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("Canvas encode gagal"));
        resolve(new File([blob], file.name.replace(/\.(png|heic|heif|webp)$/i, ".jpg"), { type: "image/jpeg" }));
      }, "image/jpeg", quality);
    };
    img.onerror = () => reject(new Error("Gambar tidak bisa dibaca"));
    img.src = URL.createObjectURL(file);
  });
}

export default function PublicBilling() {
  const { token } = useParams();
  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [catalog, setCatalog] = useState({ items: [], categories: [] });
  const [rows, setRows] = useState([]);           // {name, qty, price}
  const [notes, setNotes] = useState("");
  const [method, setMethod] = useState("CASH");
  const [photos, setPhotos] = useState([]);       // {file, preview, uploading, url, error}
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [jobType, setJobType] = useState("");       // "" | "workshop"
  const [warrantyStatus, setWarrantyStatus] = useState(""); // "" | "GARANSI" | "CANCEL" | "LANJUT"
  const [notesError, setNotesError] = useState(false);  // v85.46 highlight

  // v85.47 — Auto-detect job_type & warranty_status dari item yang dipilih
  // teknisi. Prioritas warranty: CANCEL > LANJUT > GARANSI (kalau multi-pick).
  useEffect(() => {
    const names = rows.map(r => (r.name || "").toLowerCase().trim());
    const cats = rows.map(r => (r.category || "").toLowerCase().trim());
    // Tipe Pekerjaan — kategori "Repair di Workshop" atau item ber-nama "Repair di Workshop"
    const isWorkshop = cats.some(c => c.includes("repair di workshop") || c === "workshop")
      || names.some(n => n === "repair di workshop");
    setJobType(isWorkshop ? "workshop" : "");
    // Status Khusus — dari item marker
    const hasCancel  = names.some(n => n === "cancel" || n === "cencel");
    const hasLanjut  = names.some(n => n.includes("pekerjaan dilanjut") || n.includes("pekerjaan lanjut"));
    const hasGaransi = names.some(n =>
      (n === "garansi" || n === "klaim garansi" || n.startsWith("garansi "))
      && !n.includes("dilanjut")
    );
    if (hasCancel)       setWarrantyStatus("CANCEL");
    else if (hasLanjut)  setWarrantyStatus("LANJUT");
    else if (hasGaransi) setWarrantyStatus("GARANSI");
    else                 setWarrantyStatus("");
  }, [rows]);

  useEffect(() => {
    (async () => {
      try {
        const [w, c] = await Promise.all([
          pub.get(`/billing/${token}`).then(r => r.data),
          pub.get(`/pricing`).then(r => r.data),
        ]);
        setWo(w); setCatalog(c);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Link tidak valid atau kadaluarsa.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  // v85.49 — Auto-fill item terpilih dari layanan + jumlah WO (sesuai pesanan
  // kostumer saat Quick Order). Tech tetap bisa hapus (X) atau tambah item
  // lain kalau layanan aktual beda. Hanya jalan SEKALI setelah WO load supaya
  // tidak nge-overwrite perubahan tech.
  // v85.50 — Harga juga di-auto-fill dari katalog (pilih item pertama di
  // kategori yg match service_type). Tech bisa edit langsung di field harga.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!wo || prefilledRef.current) return;
    // Butuh catalog juga supaya price lookup bisa jalan. Kalau catalog belum
    // ready, tunggu render berikutnya.
    if (!catalog || !Array.isArray(catalog.items)) return;
    // Foto awal pekerjaan dari aplikasi teknisi (tombol START) → tampil sbg foto terupload
    if (Array.isArray(wo.start_photos) && wo.start_photos.length) {
      setPhotos(wo.start_photos.slice(0, 3).map(ph => ({
        preview: ph.id ? `https://drive.google.com/thumbnail?id=${ph.id}&sz=w400` : "",
        uploading: false, url: ph.webViewLink, drive_id: ph.id,
      })));
    }
    // Dari tombol Cancel aplikasi teknisi (?preset=cancel) → item "Cancel" langsung terisi
    if (new URLSearchParams(window.location.search).get("preset") === "cancel") {
      const cancelItem = (catalog.items || []).find(it => /^c[ae]ncel$/i.test((it.name || "").trim()));
      if (cancelItem) {
        setRows([{ name: cancelItem.name, qty: 1, price: cancelItem.price || 0, category: cancelItem.category || null,
                   warranty_key: null, warranty_label: null, warranty_coverage: null, warranty_extras: [] }]);
      }
      prefilledRef.current = true;
      return;
    }
    // Kalau billing sudah tersimpan sebelumnya (resubmit), biarkan field kosong
    // supaya tech bisa isi ulang dari scratch — logika resubmit tidak diubah.
    if (wo.billing && Array.isArray(wo.billing.items) && wo.billing.items.length > 0) {
      prefilledRef.current = true;
      return;
    }
    const st = (wo.service_type || "").toUpperCase();
    const qty = Math.max(1, parseInt(wo.quantity, 10) || 1);
    // v85.51 — Auto-fill HANYA utk layanan "service" / "cleaning". Jenis layanan
    // lain (PASANG, PERBAIKAN, dsb) biarkan kosong supaya tech pilih item spesifik
    // dari katalog (varian PK beda-beda harganya + commission-ID).
    if (st !== "CLEANING" && st !== "SERVICE") {
      prefilledRef.current = true;
      return;
    }
    // Lookup item catalog spesifik — PENTING: pakai `defaultItem.name` LENGKAP
    // (termasuk kode LCxxx di akhir) supaya perhitungan komisi teknisi bisa jalan.
    // Hard-coded display name sebelumnya ("SERVICE") menyebabkan commission = 0.
    const pattern = /cleaning\s*ac\s*split/i;
    const defaultItem = (catalog.items || []).find(
      it => pattern.test(it.name || "") && it.active !== false
    );
    if (!defaultItem) {
      // Katalog belum punya item cleaning cocok → skip auto-fill
      prefilledRef.current = true;
      return;
    }
    setRows([{
      name: defaultItem.name,            // ← nama LENGKAP dgn kode LCxxx
      qty,
      price: defaultItem.price || 0,
      category: defaultItem.category || null,
      warranty_key: defaultItem.warranty_key || null,
      warranty_label: defaultItem.warranty_label || null,
      warranty_coverage: defaultItem.warranty_coverage || null,
      warranty_extras: defaultItem.warranty_extras || [],
    }]);
    prefilledRef.current = true;
  }, [wo, catalog]);

  function addFromCatalog(it) {
    setRows(r => [...r, {
      name: it.name, qty: 1, price: it.price || 0,
      category: it.category || null,
      warranty_key: it.warranty_key || null,
      warranty_label: it.warranty_label || null,
      warranty_coverage: it.warranty_coverage || null,
      warranty_extras: it.warranty_extras || [],
    }]);
  }
  function addBlank() { setRows(r => [...r, { name: "", qty: 1, price: 0, category: null, warranty_key: null, warranty_label: null, warranty_coverage: null, warranty_extras: [] }]); }
  function updateRow(i, key, val) {
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  }
  function delRow(i) { setRows(rs => rs.filter((_, idx) => idx !== i)); }

  const total = rows.reduce((sum, r) => sum + (Number(r.qty) || 0) * (Number(r.price) || 0), 0);

  // ── Photo handling ─────────────────────────────────────────────────
  async function onPickPhotos(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-pick same file
    await addPhotos(files, true);
  }
  // App mobile teknisi: kamera in-app (foto sudah dikompres saat capture) → tanpa kompres ulang.
  const inApp = typeof window !== "undefined" && !!window.ReactNativeWebView;
  useEffect(() => {
    window.__gacAddPhoto = (b64, name) => {
      const bin = atob(b64);
      const buf = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
      addPhotos([new File([buf], name || `foto-${Date.now()}.jpg`, { type: "image/jpeg" })], false);
    };
    // App terbaru: foto sudah diupload native (multipart) → hanya terima URL Drive.
    window.__gacAddPhotoUrl = (url, id) => setPhotos(p => p.length >= 3 ? p : [...p, {
      preview: id ? `https://drive.google.com/thumbnail?id=${id}&sz=w400` : "", uploading: false, url, drive_id: id || undefined,
    }]);
    // Foto diantre di HP (upload di belakang) → slot "menunggu kirim", dihitung memenuhi syarat foto.
    window.__gacAddPendingPhoto = (localId) => setPhotos(p => (p.length >= 3 || p.some(x => x.local_id === localId)) ? p
      : [...p, { preview: "", uploading: false, pending: true, local_id: localId }]);
    window.__gacResolvePhoto = (localId, url, id) => setPhotos(p => p.map(x => x.local_id === localId ? {
      ...x, pending: false, url, drive_id: id || undefined,
      preview: id ? `https://drive.google.com/thumbnail?id=${id}&sz=w400` : "",
    } : x));
    return () => { delete window.__gacAddPhoto; delete window.__gacAddPhotoUrl; delete window.__gacAddPendingPhoto; delete window.__gacResolvePhoto; };
  });
  const readySentRef = useRef(false);
  useEffect(() => {
    if (!inApp || !wo || readySentRef.current) return;
    readySentRef.current = true;
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "billing_ready" }));
  }, [inApp, wo]);
  function openAppCamera(e) {
    e.preventDefault();
    if (photos.length >= 3) { toast.warning("Maks 3 foto"); return; }
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "open_camera" }));
  }
  async function addPhotos(files, compress) {
    const slots = 3 - photos.length;
    if (slots <= 0) { toast.warning("Maks 3 foto"); return; }
    const chosen = files.slice(0, slots);
    for (const f of chosen) {
      const stub = { file: f, preview: URL.createObjectURL(f), uploading: true };
      setPhotos(p => [...p, stub]);
      try {
        const compressed = compress ? await compressImage(f) : f;
        const form = new FormData();
        form.append("files", compressed);
        const r = await pub.post(`/billing/${token}/upload-photos`, form,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        const first = (r.data.uploaded || [])[0];
        if (!first || !first.webViewLink) {
          const fail = (r.data.failed || [])[0];
          setPhotos(p => p.map(x => x === stub ? { ...x, uploading: false, error: fail?.error || "Upload gagal" } : x));
          toast.error(fail?.error || "Upload foto gagal");
        } else {
          setPhotos(p => p.map(x => x === stub ? { ...x, uploading: false, url: first.webViewLink, drive_id: first.id } : x));
        }
      } catch (err) {
        setPhotos(p => p.map(x => x === stub ? { ...x, uploading: false, error: err?.response?.data?.detail || err.message } : x));
        toast.error("Upload foto gagal: " + (err?.response?.data?.detail || err.message));
      }
    }
  }
  function delPhoto(idx) { setPhotos(p => p.filter((_, i) => i !== idx)); }

  async function submit() {
    if (rows.length === 0) { toast.error("Tambah minimal 1 item pengerjaan"); return; }
    if (rows.some(r => !r.name.trim())) { toast.error("Nama item tidak boleh kosong"); return; }
    if (photos.some(p => p.uploading)) { toast.warning("Tunggu upload foto selesai"); return; }
    // v85.46 — Catatan WAJIB utk status khusus (Cancel / Garansi / Pekerjaan Dilanjutkan)
    const isSpecial = ["CANCEL", "GARANSI", "LANJUT"].includes(warrantyStatus);
    if (isSpecial && !notes.trim()) {
      setNotesError(true);
      toast.error("Silakan isi kolom keterangan");
      return;
    }
    setSubmitting(true);
    try {
      const photo_urls = photos.filter(p => p.url).map(p => p.url);
      const pending_photo_ids = photos.filter(p => p.pending && !p.url).map(p => p.local_id);
      const r = await pub.post(`/billing/${token}/submit`, {
        items: rows.map(x => ({
          name: x.name, qty: Number(x.qty) || 1, price: Number(x.price) || 0,
          category: x.category || null, warranty_key: x.warranty_key || null,
        })),
        notes: notes.trim(),
        payment_method: method,
        photo_urls,
        pending_photo_ids,
        job_type: jobType || null,
        warranty_status: warrantyStatus || null,
      });
      setResult(r.data);
      const wa = r.data.whatsapp || {};
      if (wa.ok || wa.simulated) toast.success("Invoice terkirim ke WhatsApp customer ✓");
      else if (wa.skipped) toast.warning(`Invoice tersimpan di kalender. WA tidak dikirim: ${wa.skipped}`, {duration: 10000});
      else toast.warning(`Invoice tersimpan di kalender. WA GAGAL: ${wa.error || "Unknown"}`, {duration: 12000});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal submit — coba lagi");
    } finally { setSubmitting(false); }
  }

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-zinc-50">
      <div className="text-sm text-zinc-500">Memuat data pekerjaan…</div>
    </div>
  );
  if (err) return (
    <div className="min-h-screen grid place-items-center bg-zinc-50 p-4">
      <div className="max-w-sm w-full bg-white border border-red-200 rounded-sm p-6 text-center">
        <AlertCircle className="w-10 h-10 mx-auto mb-2 text-red-500" />
        <div className="font-semibold text-red-600">Link Tidak Valid</div>
        <div className="text-[13px] text-zinc-600 mt-2">{err}</div>
      </div>
    </div>
  );

  if (result) return (
    <div className="min-h-screen bg-zinc-50 p-4">
      <div className="max-w-lg mx-auto bg-white border border-emerald-200 rounded-sm p-6" data-testid="billing-success">
        <div className="text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-2" />
          <div className="text-xl font-bold text-emerald-700">Invoice Terkirim!</div>
          <div className="text-[13px] text-zinc-600 mt-1">Total: <b>{rp(result.grand_total)}</b></div>
        </div>
        <div className="mt-5 bg-zinc-50 border border-zinc-200 rounded-sm p-3 whitespace-pre-wrap text-[11px] font-mono leading-relaxed overflow-x-auto">
          {result.invoice_text}
        </div>
        <div className="mt-4 space-y-1 text-[11px]">
          <div className={result.gcal_patched ? "text-emerald-700" : "text-amber-600"}>
            {result.gcal_patched ? "✓ Deskripsi Google Calendar diperbarui" : "⚠ GCal update: " + (result.gcal_error || "gagal")}
          </div>
          <div className={result.whatsapp?.ok || result.whatsapp?.simulated ? "text-emerald-700" : "text-amber-600"}>
            {result.whatsapp?.ok ? "✓ Terkirim ke WhatsApp customer" :
             result.whatsapp?.simulated ? "✓ (Simulator) — WA akan terkirim di production" :
             result.whatsapp?.skipped ? "⚠ " + result.whatsapp.skipped :
             "⚠ WA gagal: " + (result.whatsapp?.error || "Unknown")}
          </div>
          {(result.photo_urls || []).length > 0 && (
            <div className="text-emerald-700">✓ {result.photo_urls.length} foto di Google Drive</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-50 p-4 pb-32" data-testid="public-billing-page">
      <div className="max-w-lg mx-auto space-y-3">
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="bg-[#E65100] text-white rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest opacity-80">GLOBAL AC · Field Billing</div>
          <div className="text-lg font-bold leading-tight mt-1">{wo.customer_name}</div>
          <div className="text-[12px] opacity-90 mt-1">{wo.address}</div>
          <div className="text-[11px] opacity-80 mt-2">
            {wo.service_type} · {wo.quantity} unit
          </div>
          {wo.scheduled_start && (
            <div className="text-[10px] opacity-70 mt-1 font-mono">
              {wo.scheduled_start.slice(0,10)} · {wo.scheduled_start.slice(11,16)}–{wo.scheduled_end?.slice(11,16)}
            </div>
          )}
        </div>

        {/* ── Rincian Pengerjaan ─────────────────────────────── */}
        <SectionCard title="Rincian Pengerjaan">
          <ItemSearchPicker items={catalog.items} categories={catalog.categories} onPick={addFromCatalog} />

          <div className="mt-3 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Item Terpilih</div>
            <Button size="sm" onClick={addBlank} className="h-7 rounded-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 gap-1" data-testid="add-blank-btn">
              <Plus className="w-3 h-3" /> Manual
            </Button>
          </div>
          {rows.length === 0 && (
            <div className="text-[11px] text-zinc-400 text-center py-4 border border-dashed border-zinc-200 rounded-sm mt-2">
              Belum ada item — cari & pilih dari daftar tarif di atas.
            </div>
          )}
          <div className="space-y-1.5 mt-2">
            {rows.map((r, i) => (
              <div key={i} data-testid={`bill-row-${i}`}>
                <div className="grid grid-cols-[1fr_55px_100px_28px] gap-1.5 items-center">
                  <Input value={r.name} onChange={e=>updateRow(i,"name",e.target.value)}
                    placeholder="Nama item" className="rounded-sm text-[12px] h-8"/>
                  <Input type="number" value={r.qty} onChange={e=>updateRow(i,"qty",e.target.value)}
                    className="rounded-sm text-[12px] h-8 text-center" min="0" step="0.5"/>
                  <Input type="number" value={r.price} onChange={e=>updateRow(i,"price",e.target.value)}
                    className="rounded-sm text-[12px] h-8 text-right font-mono" min="0"/>
                  <button onClick={()=>delRow(i)} className="h-8 w-7 grid place-items-center text-zinc-400 hover:text-red-500" data-testid={`del-row-${i}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
                {(r.warranty_label || r.category) && (
                  <div className="mt-0.5 pl-1 space-y-0.5">
                    {r.category && (
                      <span className="inline-block text-[10px] text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded mr-1">
                        {r.category}
                      </span>
                    )}
                    {r.warranty_label && (
                      <div className="text-[10px] text-emerald-700 leading-tight">
                        <div>🛡 Garansi:</div>
                        <div className="pl-4">• <b>{r.warranty_label}</b>{r.warranty_coverage ? `: ${r.warranty_coverage.toLowerCase()}` : ""}</div>
                        {(r.warranty_extras || []).map((ex, k) => (
                          <div key={k} className="pl-4">• <b>{ex.duration_label}</b>{ex.coverage ? `: ${ex.coverage.toLowerCase()}` : ""}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionCard>

        {/* ── Payment ────────────────────────────────────────── */}
        <SectionCard title="Metode Bayar">
          <div className="flex gap-1.5">
            {["CASH","TRANSFER","QRIS"].map(m => (
              <button key={m} onClick={()=>setMethod(m)}
                className={`flex-1 h-9 rounded-sm text-[12px] font-semibold border transition-colors ${
                  method === m ? "bg-[#E65100] text-white border-[#E65100]" : "bg-white text-zinc-600 border-zinc-200 hover:border-[#E65100]"
                }`}
                data-testid={`method-${m}`}>{m}</button>
            ))}
          </div>
        </SectionCard>

        {/* v85.47 — Tipe & Status grid DIHAPUS. Keduanya di-auto-detect dari
            item yang dipilih teknisi (lihat useEffect di atas). Mode terdeteksi
            ditampilkan sbg badge info saja. */}
        {(() => {
          const isCancel = warrantyStatus === "CANCEL";
          const isGaransi = warrantyStatus === "GARANSI";
          const isLanjut = warrantyStatus === "LANJUT";
          const isWorkshop = jobType === "workshop";
          if (!isCancel && !isGaransi && !isLanjut && !isWorkshop) return null;
          const chips = [];
          if (isWorkshop) chips.push({ label: "🔧 Repair di Workshop (event hijau)", cls: "bg-emerald-600 text-white" });
          if (isCancel) chips.push({ label: "❌ Cancel (event merah, invoice tidak dikirim)", cls: "bg-red-600 text-white" });
          if (isGaransi) chips.push({ label: "🛡 Klaim Garansi (event merah, invoice tetap dikirim)", cls: "bg-red-600 text-white" });
          if (isLanjut) chips.push({ label: "⚙ Pekerjaan Dilanjutkan (event merah, invoice tidak dikirim)", cls: "bg-red-600 text-white" });
          return (
            <SectionCard title="Mode Terdeteksi">
              <div className="flex flex-col gap-1.5" data-testid="auto-mode-info">
                {chips.map((c, i) => (
                  <div key={i} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[11px] font-semibold ${c.cls}`}>
                    {c.label}
                  </div>
                ))}
                <div className="text-[10px] text-zinc-500 mt-1 italic">
                  Mode ini otomatis dari item yang Anda pilih di atas. Hapus item untuk mematikan mode.
                </div>
              </div>
            </SectionCard>
          );
        })()}

        {/* ── Notes ──────────────────────────────────────────── */}
        {(() => {
          const isSpecial = ["CANCEL", "GARANSI", "LANJUT"].includes(warrantyStatus);
          const label = isSpecial ? "Catatan (WAJIB)" : "Catatan (opsional)";
          return (
            <SectionCard title={label}>
              <Textarea value={notes}
                onChange={e=>{ setNotes(e.target.value); if (notesError) setNotesError(false); }}
                placeholder={isSpecial
                  ? "Wajib dijelaskan: alasan cancel / detail garansi / kenapa pekerjaan dilanjutkan…"
                  : "mis. Freon ditambah 0.3kg, garansi 30 hari, dsb."}
                rows={isSpecial ? 3 : 2}
                className={`rounded-sm text-[12px] ${notesError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                data-testid="notes-input"/>
              {isSpecial && !notes.trim() && (
                <div className="text-[11px] text-red-600 mt-1.5" data-testid="notes-required-hint">
                  Keterangan wajib diisi — admin perlu alasan supaya bisa menindaklanjuti.
                </div>
              )}
            </SectionCard>
          );
        })()}

        {/* ── Photos ─────────────────────────────────────────── */}
        <SectionCard title={`Foto Bukti Servis (${photos.length}/3)`}>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-sm overflow-hidden bg-zinc-100 border border-zinc-200"
                   data-testid={`photo-slot-${i}`}>
                {p.preview && <img src={p.preview} alt="" className="w-full h-full object-cover" />}
                {p.pending && !p.url && (
                  <div className="absolute inset-0 grid place-items-center p-1 text-center" data-testid={`photo-pending-${i}`}>
                    <div>
                      <CloudUpload className="w-6 h-6 mx-auto text-amber-500" />
                      <div className="text-[9px] text-zinc-500 mt-1 leading-tight">Tersimpan di HP<br/>menunggu kirim</div>
                    </div>
                  </div>
                )}
                {p.uploading && (
                  <div className="absolute inset-0 bg-black/50 grid place-items-center">
                    <Loader2 className="w-5 h-5 text-white animate-spin"/>
                  </div>
                )}
                {p.error && (
                  <div className="absolute inset-0 bg-red-500/80 grid place-items-center p-1 text-[9px] text-white text-center">
                    {p.error}
                  </div>
                )}
                {p.url && !p.uploading && (
                  <div className="absolute top-1 left-1 bg-emerald-500 rounded-full p-0.5">
                    <CheckCircle2 className="w-3 h-3 text-white"/>
                  </div>
                )}
                <button onClick={()=>delPhoto(i)}
                  className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full w-5 h-5 grid place-items-center"
                  data-testid={`del-photo-${i}`}>
                  <X className="w-3 h-3 text-white"/>
                </button>
              </div>
            ))}
            {photos.length < 3 && (
              <label className="aspect-square rounded-sm border-2 border-dashed border-zinc-300 hover:border-[#E65100] hover:bg-orange-50 grid place-items-center cursor-pointer transition-colors"
                     data-testid="add-photo-btn" onClick={inApp ? openAppCamera : undefined}>
                <div className="text-center">
                  <Camera className="w-6 h-6 mx-auto text-zinc-400" />
                  <div className="text-[10px] text-zinc-500 mt-1">Tambah</div>
                </div>
                <input type="file" accept="image/*" multiple capture="environment"
                  className="hidden" onChange={onPickPhotos} />
              </label>
            )}
          </div>
          <div className="text-[10px] text-zinc-400 mt-2">
            Foto akan diunggah ke Google Drive (folder bulanan) dan URL dilampirkan ke invoice WhatsApp.
          </div>
        </SectionCard>

        {/* ── Sticky Total + Submit ──────────────────────────── */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#E65100] shadow-2xl">
          <div className="max-w-lg mx-auto p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total</div>
              <div className="text-2xl font-bold text-[#E65100] font-mono" data-testid="total-amount">{rp(total)}</div>
            </div>
            {/* v84.28 / v85.46 — Guards:
                 - Foto min 1 wajib untuk Garansi/Pekerjaan Lanjut/Normal (Cancel bebas)
                 - Catatan wajib utk status khusus (Cancel/Garansi/Lanjut)
                 - WA dikirim ke customer kecuali Cancel / Lanjut (Garansi tetap kirim) */}
            {(() => {
              const isCancel = warrantyStatus === "CANCEL";
              const isGaransi = warrantyStatus === "GARANSI";
              const isLanjut = warrantyStatus === "LANJUT";
              const isSpecial = isCancel || isGaransi || isLanjut;
              const uploadedPhotos = photos.filter(p => p.url || p.pending).length;
              const uploadingCount = photos.filter(p => p.uploading).length;
              const notesMissing = isSpecial && !notes.trim();
              const photoMissing = (!isCancel) && uploadedPhotos < 1;
              const disabled = submitting || rows.length === 0
                || uploadingCount > 0
                || photoMissing
                || notesMissing;
              let hint = null;
              if (uploadingCount > 0) {
                hint = <div className="text-[11px] text-amber-700 mb-1.5 text-center">⏳ Menunggu upload {uploadingCount} foto selesai…</div>;
              } else if (notesMissing) {
                hint = <div className="text-[11px] text-red-600 mb-1.5 text-center font-medium" data-testid="submit-notes-missing">⚠️ Silakan isi kolom keterangan dulu</div>;
              } else if (photoMissing) {
                hint = <div className="text-[11px] text-red-600 mb-1.5 text-center font-medium" data-testid="photo-required-hint">⚠️ Foto bukti pekerjaan wajib minimal 1</div>;
              } else if (isCancel) {
                hint = <div className="text-[11px] text-zinc-500 mb-1.5 text-center italic">Billing CANCEL — invoice TIDAK dikirim ke customer</div>;
              } else if (isLanjut) {
                hint = <div className="text-[11px] text-zinc-500 mb-1.5 text-center italic">Pekerjaan Dilanjutkan — invoice TIDAK dikirim ke customer, admin akan menindaklanjuti</div>;
              } else if (isGaransi) {
                hint = <div className="text-[11px] text-emerald-700 mb-1.5 text-center italic">Klaim Garansi — invoice tetap dikirim ke customer (bukti + catatan)</div>;
              }
              const btnColor = isCancel ? 'bg-zinc-600 hover:bg-zinc-700'
                              : isLanjut ? 'bg-slate-700 hover:bg-slate-800'
                              : 'bg-[#E65100] hover:bg-[#F57C00]';
              const btnLabel = submitting ? "Mengirim…"
                              : isCancel ? "Simpan sbg CANCEL (tidak kirim WA)"
                              : isLanjut ? "Simpan sbg LANJUT (tidak kirim WA)"
                              : "Kirim Invoice ke WhatsApp →";
              return (
                <>
                  {hint}
                  <Button onClick={submit} disabled={disabled}
                    className={`w-full h-11 rounded-sm text-[13px] font-bold ${btnColor}`}
                    data-testid="submit-billing-btn">
                    {btnLabel}
                  </Button>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function SectionCard({ title, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-3">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Search + Dropdown picker for master_data items
function ItemSearchPicker({ items, categories, onPick }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return items.filter(it => {
      if (cat && it.category !== cat) return false;
      if (qLower && !((it.name||"").toLowerCase().includes(qLower) || (it.category||"").toLowerCase().includes(qLower))) return false;
      return true;
    }).slice(0, 30);
  }, [items, q, cat]);

  return (
    <div ref={boxRef} className="relative">
      <div className="flex gap-1.5">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input value={q} onChange={e=>{ setQ(e.target.value); setOpen(true); }}
            onFocus={()=>setOpen(true)}
            placeholder="Cari item (nama / kategori)…"
            className="rounded-sm text-[12px] h-9 pl-7"
            data-testid="tarif-search-input"/>
        </div>
        <select value={cat} onChange={e=>{ setCat(e.target.value); setOpen(true); }}
          className="rounded-sm text-[12px] h-9 border border-zinc-200 bg-white px-2 max-w-[110px]"
          data-testid="tarif-category-select">
          <option value="">Semua</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-xl max-h-72 overflow-y-auto"
             data-testid="tarif-dropdown">
          {filtered.length === 0 && (
            <div className="text-[11px] text-zinc-400 text-center py-4">Tidak ditemukan</div>
          )}
          {filtered.map((it, i) => (
            <button key={i}
              onClick={()=>{ onPick(it); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-orange-50 border-b border-zinc-100 last:border-b-0"
              data-testid={`tarif-option-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-[12px] text-zinc-800 leading-tight">{it.name}</div>
                <div className="text-[12px] font-mono font-semibold text-[#E65100] whitespace-nowrap">{rp(it.price)}</div>
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {it.category}{it.unit ? ` · ${it.unit}` : ""}
                {it.warranty_label && <span className="text-emerald-700 ml-1">· 🛡 {it.warranty_label}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
